import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LockKey, TrendUp, Storefront, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs, offerList, localBusiness } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { NotifyMe } from "@/components/NotifyMe";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ALT = {
  fruit_basket: "A wicker basket of fresh fruit — apples, oranges, bananas, grapes and berries — on a kitchen table",
  salad_bowl: "A fresh salad bowl of leaves, tomato, cucumber and peppers on a kitchen table",
  weekly_shop: "A week's groceries unpacked onto a kitchen table — milk, eggs, bread, rice, vegetables and fruit",
  deals: "A supermarket aisle with shelf price labels along the shelves",
  inflation: "A paper till receipt and coins on a kitchen table beside a notebook of prices",
};

export default function PriceLockPublic() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/lock/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ sections: [] }));
  }, []);

  const t = board?.tracker;
  const up = (t?.change || 0) > 0;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbs([["Home", "/"], ["Price lock", "/price-lock"]]),
      localBusiness({
        name: "Unity & Utility price lock",
        description: "Lock your regular grocery basket at today's price — fruit basket, salad bowl, milk, eggs, bread, rice and the weekly veg — scheduled by AI, priced across every store, with price-drop protection.",
        path: "/price-lock",
        image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
        type: "GroceryStore",
        area: "Worldwide",
      }),
      offerList("Lock these at today's price", "/price-lock",
        (board?.cheapest?.lines || []).map((l) => ({
          name: l.item,
          description: `Per ${l.pack} · locked at ${l.lock_discount_pct}% off today's shelf price`,
          price: l.locked, symbol: board?.symbol, unit: `per ${l.pack}`,
        }))),
    ],
  };

  useSeo({
    title: "Price lock — buy your regular shop at today's price and beat the next rise | Unity & Utility",
    description: "Lock the fruit basket, the salad bowl, milk, eggs, bread, rice and the weekly veg at today's price for 3, 6 or 12 months. An AI works out the schedule and quantities for your household, the same basket is priced across every store so you take the cheapest, and if a shelf price ever falls below your lock you pay the lower one. Pay per drop, pause, skip or cancel any time.",
    path: "/price-lock",
    image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
    jsonLd,
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="lock-public-page">
        <Crumbs items={[["Home", "/"], ["Price lock", "/price-lock"]]} />
        <p className="overline text-[#0B5C2E]">Price lock</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <LockKey size={34} weight="duotone" className="mt-1 shrink-0 text-[#0B5C2E]" />
          Hold your shop at today's price
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="lock-public-intro">
          The fruit basket, the salad bowl, milk, eggs, bread, rice and the weekly veg — bought
          ahead and held at the price they are today, for three, six or twelve months. Tell the
          assistant who it is feeding and it works the schedule out for you: what, how much, how
          often it should land and how long is worth locking. The same basket is then priced at
          every store so you take the cheapest, and if a shelf price ever falls below your lock
          you pay the lower one. Pay per drop, never the whole term up front, and pause, skip or
          cancel whenever you like. Pictures here are redrawn by AI every 24 hours.
        </p>
        <div className="mt-5">
          <ShareBar
            url={board?.share_token
              ? `${window.location.origin}/p/${board.share_token}`
              : `${window.location.origin}/price-lock`}
            text="Lock the weekly shop at today's price and let the next rise pass you by."
            testid="lock-public-share" instagram marketplace />
        </div>

        {board?.image_url && (
          <img src={`${BACKEND}${board.image_url}`} alt={ALT.fruit_basket} loading="lazy"
            className="mt-8 h-56 w-full border border-border object-cover sm:h-80"
            data-testid="lock-public-image" />
        )}

        {t && (
          <section className="mt-6 border border-[#0B5C2E] bg-[#F3FAF5] p-5" data-testid="lock-public-tracker">
            <p className="overline text-[#0B5C2E]">
              <TrendUp size={14} weight="bold" className="mr-1 inline" />
              The basket tracker{board?.day ? ` · ${board.day}` : ""}
              {board?.auto_posted && <span className="ml-2">Posted to Facebook</span>}
            </p>
            <div className="mt-3 grid grid-cols-3 gap-3">
              <div className="border border-border bg-white p-3" data-testid="lock-stat-then">
                <p className="font-display text-2xl font-bold text-[#0F172A]">
                  {board.symbol}{Number(t.basket_when_started).toFixed(2)}
                </p>
                <p className="text-xs text-slate-500">{t.items} items on {t.since}</p>
              </div>
              <div className="border border-border bg-white p-3" data-testid="lock-stat-now">
                <p className="font-display text-2xl font-bold text-[#0F172A]">
                  {board.symbol}{Number(t.basket_today).toFixed(2)}
                </p>
                <p className="text-xs text-slate-500">The same basket today</p>
              </div>
              <div className="border border-border bg-white p-3" data-testid="lock-stat-change">
                <p className={`font-display text-2xl font-bold ${up ? "text-[#B3241B]" : "text-[#0B5C2E]"}`}>
                  {up ? "+" : ""}{Number(t.change_pct).toFixed(1)}%
                </p>
                <p className="text-xs text-slate-500">
                  {up ? "Up since the board started" : "Level or down since the board started"}
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Anybody who locked that basket on {t.since} is still paying the left hand figure.
              That is the whole point of it.
            </p>
          </section>
        )}

        {(board?.sections || []).map((s) => (
          <section key={s.key} className="mt-10" data-testid={`lock-public-section-${s.key}`}>
            <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
              {s.label}
            </h2>
            {board?.section_images?.[s.key] && (
              <img src={`${BACKEND}${board.section_images[s.key]}`} alt={ALT[s.key] || s.label}
                loading="lazy" className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
                data-testid={`lock-public-image-${s.key}`} />
            )}
            <div className="mt-3">
              <ShareBar url={`${window.location.origin}/price-lock`}
                text={`${s.label} — locked at today's price with Unity & Utility.`}
                testid={`lock-public-share-${s.key}`} compact instagram />
            </div>
          </section>
        ))}

        <section className="mt-10" data-testid="lock-public-stores">
          <h2 className="font-display flex items-center gap-2 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            <Storefront size={18} weight="duotone" className="text-[#002FA7]" />
            The same basket, store by store
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            {t?.items} everyday items, priced at each store today and at the six month lock price.
          </p>
          <div className="mt-3 space-y-2">
            {(board?.stores || []).map((s, i) => (
              <div key={s.store} className="flex items-center justify-between gap-3 border border-border bg-white px-4 py-3"
                data-testid={`lock-public-store-${i}`}>
                  <span className="min-w-0">
                    <span className="block font-semibold text-[#0F172A]">{s.store}</span>
                    <span className="block text-xs text-slate-500">
                      {s.published_lines > 0
                        ? (i === 0 ? "Cheapest for this basket today"
                          : `Saves ${board.symbol}${Number(s.saving).toFixed(2)} by locking`)
                        : "Guide price — this store has not published its own yet"}
                    </span>
                  </span>
                <span className="shrink-0 text-right">
                  <span className="block font-display text-lg font-bold text-[#0B5C2E]">
                    {board.symbol}{Number(s.locked_total).toFixed(2)}
                  </span>
                  <span className="block text-xs text-slate-400 line-through">
                    {board.symbol}{Number(s.today_total).toFixed(2)}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 border-l-2 border-[#0B5C2E] bg-[#F3FAF5] px-4 py-4" data-testid="lock-public-how">
          <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
            <Sparkle size={18} weight="duotone" className="text-[#0B5C2E]" />
            How the lock works
          </h2>
          <ul className="mt-2 space-y-1.5 text-base leading-relaxed text-slate-700">
            <li>Say who it is feeding — the assistant builds the basket and the schedule.</li>
            <li>Weekly, fortnightly or monthly drops, locked for 3, 6 or 12 months.</li>
            <li>Longer lock, bigger discount: {Object.entries(board?.term_discount || {}).map(([m, pct]) => `${m} months ${pct}% off`).join(", ")}. A store can better it.</li>
            <li>Every item is frozen at its own price on the day you lock it.</li>
            <li>Price-drop protection: if the shelf falls below your lock, you pay the shelf.</li>
            <li>Short on the day? It is swapped for the nearest thing at your locked price, or credited back.</li>
            <li>A credit lands on your grocery card and comes off the next drop on its own.</li>
            <li>Pay per drop, online or on handover. Pause, skip a week or cancel any time.</li>
          </ul>
          <Link to="/price-lock-desk" data-testid="lock-public-open-desk"
            className="tap-safe mt-3 inline-flex items-center text-sm font-semibold text-[#0B5C2E] underline">
            Open the price lock desk
          </Link>
        </section>

        <div className="mt-10">
          <NotifyMe source="price-lock" testid="lock-public-notify"
            blurb="Stores are publishing lock prices all the time. Leave your email and the town you shop in, and we will tell you when a store near you starts holding prices." />
        </div>
      </div>
    </PublicShell>
  );
}
